/**
 * IdentityAvatar — navy initials circle for the human side of a conversation
 * (contact rows, live-agent handoff cards). Optional presence dot mirrors a
 * HubSpot "online" signal. Purely presentational.
 */
import { cn } from '@/lib/utils';

const PRESENCE_DOT_SIZE = 9;
/** Literal per spec — distinct from V3_COLORS.liveGreen (#1F8A5B), which
 *  backs the separate "live" badge elsewhere. Not reconciled to that token
 *  since the spec calls out this exact hex for the avatar's presence dot. */
const PRESENCE_DOT_COLOR = '#1FA068';

export interface IdentityAvatarProps {
  initials: string;
  /** Diameter in px. Defaults to 36. */
  size?: number;
  online?: boolean;
  /** Color of the presence dot's ring border-[1px] — should match whatever
   *  surface the avatar sits on. Defaults to white. */
  dotRingColor?: string;
  className?: string;
}

export function IdentityAvatar({
  initials,
  size = 36,
  online = false,
  dotRingColor = '#fff',
  className,
}: IdentityAvatarProps) {
  return (
    <span
      className={cn('relative inline-flex shrink-0', className)}
      style={{ width: size, height: size }}
    >
      <span
        className="flex h-full w-full items-center justify-center rounded-full font-['Work_Sans'] font-semibold text-white"
        style={{ backgroundColor: '#31364E', fontSize: size * 0.4 }}
      >
        {initials}
      </span>
      {online && (
        <span
          aria-hidden="true"
          className="absolute rounded-full"
          style={{
            width: PRESENCE_DOT_SIZE,
            height: PRESENCE_DOT_SIZE,
            right: -1,
            bottom: -1,
            backgroundColor: PRESENCE_DOT_COLOR,
            border: `2px solid ${dotRingColor}`,
          }}
        />
      )}
    </span>
  );
}
